import * as dt from '@/common/dateUtils'
import ApiService from '@/common/api.service'
import store from '@/store'
import * as monitoringModuleHelpers from '@/store/monitoring/monitoringModule.helpers'
import * as mapUtils from '@/maps/google/mapUtils'
import * as assetMarkers from '@/maps/google/assetMarkers'
import * as googleMaps from '@/common/googleMaps'
import * as positionReplayWidgetHelpers from '@/store/monitoring/positionReplayWidget.helpers'
import * as eventMarkers from '@/maps/google/eventMarkers'

let _telemetry = []
let _polyLine = null
let _outerPolyline = null
let _replayAsset = null
let _replayInterval = null
let _eventMarkers = []

const _tripColor = '#e53935'

const plotTelemetry = (strokeColor) => {
  const coordinates = []

  if (_polyLine !== null) {
    _polyLine.setMap(null)
    _polyLine = null
  }

  _telemetry.forEach((position) => {
    coordinates.push({ lat: position.gps.latitude, lng: position.gps.longitude })
  })

  _outerPolyline = new google.maps.Polyline({
    path: coordinates,
    geodesic: true,
    strokeColor: '#ffffff',
    strokeOpacity: 0.7,
    strokeWeight: 5,
    zIndex: 99
  })
  _outerPolyline.setMap(googleMaps.map)

  _polyLine = new google.maps.Polyline({
    path: coordinates,
    geodesic: true,
    strokeColor: strokeColor,
    strokeOpacity: 0.7,
    strokeWeight: 2,
    zIndex: 100,
    icons: []
  })
  _polyLine.setMap(googleMaps.map)
}

const clearTelemetryData = () => {
  if (_polyLine !== null) {
    _polyLine.setMap(null)
    _polyLine = null
  }

  if (_outerPolyline !== null) {
    _outerPolyline.setMap(null)
    _outerPolyline = null
  }

  if (_replayAsset !== null) {
    monitoringModuleHelpers.removeMarker(_replayAsset, 'replay')
    _replayAsset = null
  }
}

const replayTick = () => {
  const replaySpeed = positionReplayWidgetHelpers.getReplaySpeed()
  if (replaySpeed > 0) {
    positionReplayWidgetHelpers.incRecordIdx()
  } else if (replaySpeed < 0) {
    positionReplayWidgetHelpers.decRecordIdx()
  }
}

const cancelReplayTick = () => {
  if (_replayInterval !== null) {
    window.clearInterval(_replayInterval)
  }
}

const plotEventMarkers = () => {
  let { markers, tooManyIcons } = eventMarkers.plotEventMarkers(_telemetry, eventMarkerClicked)
  _eventMarkers = markers
  positionReplayWidgetHelpers.setTooManyIcons(tooManyIcons)
}

const clearEventMarkers = () => {
  _eventMarkers.forEach(marker => {
    mapUtils.removeCustomMarker(marker)
    marker = null
  })
  _eventMarkers = []
}

// We intentionally do not use an arrow function callback here
// so that we can use the "this" context of the function instead
// of the module.
function eventMarkerClicked () {
  let marker = this
  positionReplayWidgetHelpers.setReplayMode('pause')
  positionReplayWidgetHelpers.setRecordIdx(marker.data.positionIndex, true)
  positionReplayWidgetHelpers.setViewMode('replay')
}

export const state = {
  vehicle: null, // Vehicle which telemetry history are being viewed
  unit: null, // Unit which telemetry history are being viewed
  date: dt.dateShort(dt.now()), // Currently viewed date
  fromTime: dt.timeShort(dt.now().subtract(1, 'hours')),
  toTime: dt.timeShort(dt.now()),
  recordIdx: 0,
  recordCount: 0,
  record: null,
  viewMode: 'replay', // Can be 'replay' (date & time selection + current position), 'options'
  isFirstRecord: true,
  isLastRecord: false,
  tooManyIcons: false,
  replayProgress: 0, // Progress percentage within the set of telemetry data
  replaySpeed: 0,
  replayStepSize: 0,
  replayMode: 'pause', // Can be 'pause', 'play', 'rewind', 'fast-forward'
  prevQueriedVehicleId: null,
  prevQueriedAssignedAs: null,
  prevQueriedUnitId: null,
  prevQueriedDate: null,
  prevQueriedFromTime: null,
  prevQueriedToTime: null
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  getVehicle (state) {
    return state.vehicle
  },

  getUnit (state) {
    return state.unit
  },

  getDate (state) {
    return state.date
  },

  getFromTime (state) {
    return state.fromTime
  },

  getToTime (state) {
    return state.toTime
  },

  getViewMode (state) {
    return state.viewMode
  },

  getReplaySpeed (state) {
    return state.replaySpeed
  },

  getIsFirstRecord (state) {
    return state.isFirstRecord
  },

  getIsLastRecord (state) {
    return state.isLastRecord
  },

  getIsFirstTrip (state) {
    return state.isFirstTrip
  },

  getIsLastTrip (state) {
    return state.isLastTrip
  },

  getReplayMode (state) {
    return state.replayMode
  },

  getPrevQueriedVehicleId (state) {
    return state.prevQueriedVehicleId
  },

  getPrevQueriedAssignedAs (state) {
    return state.prevQueriedAssignedAs
  },

  getPrevQueriedUnitId (state) {
    return state.prevQueriedUnitId
  },

  getPrevQueriedDate (state) {
    return state.prevQueriedDate
  },

  getPrevQueriedFromTime (state) {
    return state.prevQueriedFromTime
  },

  getPrevQueriedToTime (state) {
    return state.prevQueriedToTime
  },

  /**
   * Get the current position/telemetry for the point in time in the replay.
   *
   * @param state
   * @returns null|Object
   */
  getRecord (state) {
    return state.record
  },

  getRecordCount (state) {
    return state.recordCount
  },

  getReplayProgress (state) {
    return state.replayProgress
  },

  getTooManyIcons (state) {
    return state.tooManyIcons
  }
}

export const actions = {
  queryVehicleTelemetry (context) {
    const vehicle = context.getters['getVehicle']
    const prevQueriedVehicleId = context.getters['getPrevQueriedVehicleId']
    const prevQueriedAssignedAs = context.getters['getPrevQueriedAssignedAs']
    const prevQueriedDate = context.getters['getPrevQueriedDate']
    const prevQueriedFromTime = context.getters['getPrevQueriedFromTime']
    const prevQueriedToTime = context.getters['getPrevQueriedToTime']
    const queryDate = context.getters['getDate']
    let queryFromTime = context.getters['getFromTime']
    let queryToTime = context.getters['getToTime']

    if (queryFromTime.split(':').length === 2) {
      queryFromTime += ':00'
    }

    if (queryToTime.split(':').length === 2) {
      queryToTime += ':00'
    }
    // Only perform the query if we haven't just queried with the same arguments
    const canQuery = prevQueriedVehicleId !== vehicle.id ||
      prevQueriedAssignedAs !== vehicle.unit_assigned_as ||
      prevQueriedDate !== queryDate ||
      prevQueriedFromTime !== queryFromTime ||
      prevQueriedToTime !== queryToTime

    if (canQuery) {
      context.commit(`setPrevQueriedVehicle`, { prevQueriedVehicleId: vehicle.id, prevQueriedAssignedAs: vehicle.unit_assigned_as })
      context.commit(`setPrevQueriedUnitId`, null)
      context.commit(`setPrevQueriedDate`, queryDate)
      return ApiService
        .get(`history/telemetry/vehicle/` +
          `${vehicle.id}/${vehicle.unit_assigned_as}/date/${queryDate}/from/${queryFromTime}/to/${queryToTime}?`)
        .then((response) => {
          store.commit(`monitoring/positionReplayWidgetModule/setLoadedTelemetry`, response.data.data)
          return response
        })
    }
    return Promise.resolve(null)
  },

  queryUnitTelemetry (context) {
    const unit = context.getters['getUnit']
    const prevQueriedUnitId = context.getters['getPrevQueriedUnitId']
    const prevQueriedDate = context.getters['getPrevQueriedDate']
    const prevQueriedFromTime = context.getters['getPrevQueriedFromTime']
    const prevQueriedToTime = context.getters['getPrevQueriedToTime']
    const queryDate = context.getters['getDate']
    let queryFromTime = context.getters['getFromTime']
    let queryToTime = context.getters['getToTime']

    if (queryFromTime.split(':').length === 2) {
      queryFromTime += ':00'
    }

    if (queryToTime.split(':').length === 2) {
      queryToTime += ':00'
    }

    // Only perform the query if we haven't just queried with the same arguments
    const canQuery = prevQueriedUnitId !== unit.id ||
      prevQueriedDate !== queryDate ||
      prevQueriedFromTime !== queryFromTime ||
      prevQueriedToTime !== queryToTime

    if (canQuery) {
      context.commit(`setPrevQueriedVehicle`, { prevQueriedVehicleId: null, prevQueriedAssignedAs: null })
      context.commit(`setPrevQueriedUnitId`, unit.id)
      context.commit(`setPrevQueriedDate`, queryDate)

      return ApiService
        .get(`history/telemetry/unit/` +
          `${unit.id}/date/${queryDate}/from/${queryFromTime}/to/${queryToTime}`)
        .then((response) => {
          store.commit(`monitoring/positionReplayWidgetModule/setLoadedTelemetry`, response.data.data)
          return response
        })
    }

    return Promise.resolve(null)
  },

  queryTelemetry (context) {
    if (context.getters['getVehicle'] !== null) {
      return context.dispatch('queryVehicleTelemetry')
    } else if (context.getters['getUnit'] !== null) {
      return context.dispatch('queryUnitTelemetry')
    } else {
      return Promise.resolve(null)
    }
  }
}

export const mutations = {
  setVehicle (state, vehicle) {
    if (state.vehicle === null && vehicle !== null) {
      state.vehicle = vehicle
      state.unit = null
      store.commit('monitoring/positionReplayWidgetModule/clearTelemetry')
      store.dispatch(`monitoring/positionReplayWidgetModule/queryVehicleTelemetry`)
    } else if (state.vehicle !== null && vehicle === null) {
      state.vehicle = vehicle
      state.unit = null
      store.commit('monitoring/positionReplayWidgetModule/clearTelemetry')
    } else if (state.vehicle !== null && vehicle !== null && state.vehicle.id !== vehicle.id) {
      state.vehicle = vehicle
      state.unit = null
      store.commit('monitoring/positionReplayWidgetModule/clearTelemetry')
      store.dispatch(`monitoring/positionReplayWidgetModule/queryVehicleTelemetry`)
    }
  },

  setUnit (state, unit) {
    if (state.unit === null && unit !== null) {
      state.vehicle = null
      state.unit = unit
      store.commit('monitoring/positionReplayWidgetModule/clearTelemetry')
      store.dispatch(`monitoring/positionReplayWidgetModule/queryUnitTelemetry`)
    } else if (state.unit !== null && unit === null) {
      state.vehicle = null
      state.unit = unit
      store.commit('monitoring/positionReplayWidgetModule/clearTelemetry')
    } else if (state.unit !== null && unit !== null && state.unit.id !== unit.id) {
      state.vehicle = null
      state.unit = unit
      store.commit('monitoring/positionReplayWidgetModule/clearTelemetry')
      store.dispatch(`monitoring/positionReplayWidgetModule/queryUnitTelemetry`)
    }
  },

  setDate (state, date) {
    if (date !== state.date) {
      state.date = date
      store.commit(`monitoring/positionReplayWidgetModule/clearTelemetry`)
      return store.dispatch(`monitoring/positionReplayWidgetModule/queryTelemetry`)
    }
  },

  setFromTime (state, fromTime) {
    if (fromTime !== state.fromTime) {
      state.fromTime = fromTime
      store.commit(`monitoring/positionReplayWidgetModule/clearTelemetry`)
      return store.dispatch(`monitoring/positionReplayWidgetModule/queryTelemetry`)
    }
  },

  setToTime (state, toTime) {
    if (toTime !== state.toTime) {
      state.toTime = toTime
      store.commit(`monitoring/positionReplayWidgetModule/clearTelemetry`)
      return store.dispatch(`monitoring/positionReplayWidgetModule/queryTelemetry`)
    }
  },

  clearTelemetry (state) {
    state.record = null
    state.recordIdx = 0
    state.recordCount = 0
    state.isFirstRecord = true
    state.isLastRecord = false
    state.isFirstTrip = true
    state.isLastTrip = false
    state.replayProgress = 0
    state.replaySpeed = 0
    state.replayStepSize = 0
    state.replayMode = 'pause'
    state.tooManyIcons = false
    state.prevQueriedVehicleId = null
    state.prevQueriedAssignedAs = null
    state.prevQueriedUnitId = null
    state.prevQueriedDate = null
    state.prevQueriedFromTime = null
    state.prevQueriedToTime = null
    cancelReplayTick()
    clearTelemetryData()
    clearEventMarkers()
  },

  setLoadedTelemetry (state, telemetry) {
    _telemetry = telemetry
    state.recordCount = _telemetry.length
    if (_telemetry.length > 0) {
      state.record = _telemetry[0]
      plotTelemetry(_tripColor)
      plotEventMarkers()

      // Add/update a trip replay marker to the map for the unit/vehicle
      if (_telemetry !== null) {
        // Unfollow all vehicles so the map doesn't attempt
        // to follow a real-time vehicle while the user is
        // trying to do a replay
        monitoringModuleHelpers.unfollowAll()

        // Make all the existing assets markers on the map
        // transparent so that they don't interfere with
        // the replay's marker. When the widget closes, they
        // will be restored to normal opacity.
        assetMarkers.setAllMarkersOpacity(0.5)

        googleMaps.map.panTo({ lat: state.record.gps.latitude, lng: state.record.gps.longitude })
        let existingAsset = null
        if (state.vehicle !== null) {
          existingAsset = monitoringModuleHelpers.getMonitoredVehicle(state.vehicle.id, state.vehicle.unit_assigned_as)
        } else if (state.unit !== null) {
          existingAsset = monitoringModuleHelpers.getMonitoredUnit(state.unit.id)
        }

        if (existingAsset !== null) {
          _replayAsset = Object.assign({}, existingAsset)
          _replayAsset.telemetry = state.record
          monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
        }
      }
    } else {
      state.record = null
    }
  },

  setViewMode (state, viewMode) {
    state.viewMode = viewMode
  },

  /**
   * Sets the current replay speed.
   *
   * 0 = Paused, -1 = Rewind, 1 = Play, < -1 = Rewind Fast, > 1 Forward Fast
   *
   * @param state
   * @param replaySpeed
   */
  setReplaySpeed (state, replaySpeed) {
    state.replaySpeed = replaySpeed
    // _replaySpeed = replaySpeed
    if (replaySpeed === 0) { // pause
      state.replayStepSize = 1
      cancelReplayTick()
    } else if (replaySpeed === 1 || replaySpeed === -1) { // play/rewind
      cancelReplayTick()
      state.replayStepSize = 1
      _replayInterval = window.setInterval(replayTick, 800)
    } else if (replaySpeed === 2 || replaySpeed === -1) { // fast forward/fast rewind
      cancelReplayTick()
      state.replayStepSize = 1
      _replayInterval = window.setInterval(replayTick, 400)
    } else if (replaySpeed > 2 || replaySpeed < -2) { // faster fast-forward/rewind
      cancelReplayTick()
      state.replayStepSize += 2
      _replayInterval = window.setInterval(replayTick, 400)
    }
  },

  setReplayMode (state, replayMode) {
    if (state.replayMode !== replayMode) {
      switch (replayMode) {
        case 'pause':
          store.commit(`monitoring/positionReplayWidgetModule/setReplaySpeed`, 0)
          break

        case 'play':
          store.commit(`monitoring/positionReplayWidgetModule/setReplaySpeed`, 1)
          break

        case 'rewind':
          store.commit(`monitoring/positionReplayWidgetModule/setReplaySpeed`, -1)
          break

        case 'fast-forward':
          store.commit(`monitoring/positionReplayWidgetModule/setReplaySpeed`, 2)
          break
      }
      state.replayMode = replayMode
    }
  },

  incReplaySpeed (state) {
    store.commit(`monitoring/positionReplayWidgetModule/setReplaySpeed`, state.replaySpeed + 1)
  },

  decReplaySpeed (state) {
    store.commit(`monitoring/positionReplayWidgetModule/setReplaySpeed`, state.replaySpeed - 1)
  },

  /**
   * Increments the active record within the telemetry.
   *
   * @param state
   */
  incRecordIdx (state) {
    state.recordIdx += state.replayStepSize
    if (state.recordIdx > _telemetry.length - 1) {
      state.recordIdx = _telemetry.length - 1
      state.isLastRecord = true
      store.commit(`monitoring/positionReplayWidgetModule/setReplayMode`, 'pause')
      state.replayProgress = 100
    } else {
      state.isLastRecord = false
      state.replayProgress = Math.ceil(state.recordIdx / (_telemetry.length - 1) * 100)
      if (state.recordIdx > 0) {
        state.isFirstRecord = false
      }
    }

    _replayAsset.telemetry = _telemetry[state.recordIdx]
    monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
    store.commit(`monitoring/positionReplayWidgetModule/setRecord`, _replayAsset.telemetry)
  },

  /**
   * Decrements the active record within the telemetry.
   *
   * @param state
   */
  decRecordIdx (state) {
    state.recordIdx -= state.replayStepSize
    if (state.recordIdx < 0) {
      state.recordIdx = 0
      state.replayProgress = 0
      state.isFirstRecord = true
      state.replayProgress = 0
      store.commit(`monitoring/positionReplayWidgetModule/setReplayMode`, 'pause')
    } else {
      state.isFirstRecord = false
      state.replayProgress = Math.ceil(state.recordIdx / (_telemetry.length - 1) * 100)
      if (state.recordIdx < _telemetry[state.recordIdx] - 1) {
        state.isLastRecord = false
      }
    }

    _replayAsset.telemetry = _telemetry[state.recordIdx]
    monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
    store.commit(`monitoring/positionReplayWidgetModule/setRecord`, _replayAsset.telemetry)
  },

  setRecordIdx (state, { recordIdx, panTo }) {
    const lastRecordIdx = _telemetry.length - 1
    if (recordIdx >= 0 && state.recordIdx <= lastRecordIdx) {
      state.recordIdx = recordIdx
      if (recordIdx === 0) {
        state.isFirstRecord = true
        state.isLastRecord = false
      } else if (recordIdx === lastRecordIdx) {
        state.isLastRecord = true
        state.isFirstRecord = false
      }
      state.replayProgress = Math.ceil(state.recordIdx / (_telemetry.length - 1) * 100)
      _replayAsset.telemetry = _telemetry[state.recordIdx]
      monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
      store.commit(`monitoring/positionReplayWidgetModule/setRecord`, _replayAsset.telemetry)

      if (panTo) {
        googleMaps.panToLatLng(
          googleMaps.map,
          _replayAsset.telemetry.gps.latitude,
          _replayAsset.telemetry.gps.longitude
        )
        if (googleMaps.map.getZoom() < 10) {
          googleMaps.map.setZoom(15)
        }
      }
    }
  },

  /**
   * Set the current telemetry record for the point in time in the replay.
   *
   * @param state
   * @param record
   */
  setRecord (state, record) {
    state.record = record
  },

  selectFirstRecord (state) {
    if (state.recordCount > 0) {
      store.commit(`monitoring/positionReplayWidgetModule/setRecordIdx`, { recordIdx: 0, panTo: false })
    }
  },

  selectLastRecord (state) {
    if (state.recordCount > 0) {
      store.commit(`monitoring/positionReplayWidgetModule/setRecordIdx`, { recordIdx: state.recordCount - 1, panTo: false })
    }
  },

  setPrevQueriedVehicle (state, { prevQueriedVehicleId, prevQueriedAssignedAs }) {
    state.prevQueriedVehicleId = prevQueriedVehicleId
    state.prevQueriedAssignedAs = prevQueriedAssignedAs
  },

  setPrevQueriedUnitId (state, prevQueriedUnitId) {
    state.prevQueriedUnitId = prevQueriedUnitId
  },

  setPrevQueriedDate (state, prevQueriedDate) {
    state.prevQueriedDate = prevQueriedDate
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.vehicle = null
    state.unit = null
    state.date = dt.dateShort(dt.now())
    state.fromTime = dt.timeShort(dt.now().subtract(1, 'hours'))
    state.toTime = dt.timeShort(dt.now())

    store.commit(`monitoring/positionReplayWidgetModule/clearTelemetry`)
  },

  refreshPeriod (state) {
    state.date = dt.dateShort(dt.now())
    state.fromTime = dt.timeShort(dt.now().subtract(1, 'hours'))
    state.toTime = dt.timeShort(dt.now())
  },

  setTooManyIcons (state, tooManyIcons) {
    state.tooManyIcons = tooManyIcons
  },

  updateEventMarkers () {
    clearEventMarkers()
    plotEventMarkers()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/monitoring/positionReplayWidgetModule.js